// src/components/common/ThoughtStream.jsx
import React, { useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/* ========================
 * useLinearScroll - Custom hook for a constant-speed scroll animation.
 * Scrolls an element to the bottom as content (dependency) changes.
 * ========================*/
const useLinearScroll = (scrollRef, dependency) => {
  const animationFrameId = useRef(null);
  const lastScrollHeight = useRef(0);
  const isInteracting = useRef(false);
  const interactionTimeout = useRef(null);

  useEffect(() => {
    const element = scrollRef?.current;
    if (!element) return;

    /* ========================
     * handleInteraction - Pauses auto-scroll on user interaction.
     * Resets interaction state after a brief timeout.
     * ========================*/
    const handleInteraction = () => {
      isInteracting.current = true;
      if (interactionTimeout.current) clearTimeout(interactionTimeout.current);
      interactionTimeout.current = setTimeout(() => {
        isInteracting.current = false;
        // Check if user is at the bottom after interaction stops
        if (element.scrollTop + element.clientHeight >= element.scrollHeight - 20) {
          // Trigger a re-evaluation if new content might have arrived
          lastScrollHeight.current = 0; 
        }
      }, 150);
    };
    
    /* ========================
     * addPassiveEventListeners - Adds listeners with passive: true for scroll performance
     * See: https://www.chromestatus.com/feature/5745543795965952
     * ========================*/
    element.addEventListener('wheel', handleInteraction, { passive: true });
    element.addEventListener('touchmove', handleInteraction, { passive: true });

    return () => {
      element.removeEventListener('wheel', handleInteraction);
      element.removeEventListener('touchmove', handleInteraction);
      if (interactionTimeout.current) clearTimeout(interactionTimeout.current);
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    }
  }, [scrollRef]);

  useEffect(() => {
    if (!scrollRef?.current || isInteracting.current) return;
    const element = scrollRef.current;
    const newScrollHeight = element.scrollHeight;

    if (newScrollHeight === lastScrollHeight.current) return;

    const isNearBottom = lastScrollHeight.current === 0 || (element.scrollTop + element.clientHeight >= lastScrollHeight.current - 20);

    if (!isNearBottom) {
      lastScrollHeight.current = newScrollHeight;
      return;
    }
    
    lastScrollHeight.current = newScrollHeight;

    const start = element.scrollTop;
    const end = newScrollHeight - element.clientHeight;
    const distance = end - start;

    if (distance <= 0) return;

    const scrollSpeed = 0.4; // Pixels per millisecond. Adjust to feel right.
    const duration = Math.max(200, distance / scrollSpeed); // ensure a minimum duration
    let startTime = null;

    /* ========================
     * animateScroll - Smoothly scrolls the element over a calculated duration.
     * ========================*/
    const animateScroll = (timestamp) => {
      if (isInteracting.current) {
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        return;
      }
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      element.scrollTop = start + distance * progress;

      if (elapsed < duration) {
        animationFrameId.current = requestAnimationFrame(animateScroll);
      }
    };

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    animationFrameId.current = requestAnimationFrame(animateScroll);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [dependency, scrollRef]);
};

/* ========================
 * ThoughtStream - Renders streaming markdown content and auto-scrolls linearly.
 * Now self-contained with Tailwind-powered markdown component styling.
 * ========================*/
const ThoughtStream = ({ text, scrollRef, isDarkTheme }) => {
  useLinearScroll(scrollRef, text);

  const markdownComponents = useMemo(() => ({
    h1: (props) => <h3 className="text-sm font-semibold mt-3 mb-1 tracking-wide" style={{ color: `rgba(var(--on-surface-rgb), 0.9)` }} {...props} />,
    h2: (props) => <h3 className="text-sm font-semibold mt-3 mb-1 tracking-wide" style={{ color: `rgba(var(--on-surface-rgb), 0.9)` }} {...props} />,
    h3: (props) => <h3 className="text-sm font-semibold mt-3 mb-1 tracking-wide" style={{ color: `rgba(var(--on-surface-rgb), 0.9)` }} {...props} />,
    h4: (props) => <h3 className="text-sm font-semibold mt-3 mb-1 tracking-wide" style={{ color: `rgba(var(--on-surface-rgb), 0.9)` }} {...props} />,
    h5: (props) => <h3 className="text-sm font-semibold mt-3 mb-1 tracking-wide" style={{ color: `rgba(var(--on-surface-rgb), 0.9)` }} {...props} />,
    h6: (props) => <h3 className="text-sm font-semibold mt-3 mb-1 tracking-wide" style={{ color: `rgba(var(--on-surface-rgb), 0.9)` }} {...props} />,
    p: (props) => <p className="my-1.5" {...props} />,
    strong: (props) => <strong className="font-semibold" style={{ color: `rgba(var(--on-surface-rgb), 0.8)` }} {...props} />,
    em: (props) => <em className="italic" style={{ color: `rgba(var(--on-surface-dim-rgb), 0.9)` }} {...props} />,
    ul: (props) => <ul className="list-disc list-outside ml-4 my-2 space-y-1" {...props} />,
    ol: (props) => <ol className="list-decimal list-outside ml-4 my-2 space-y-1" {...props} />,
    li: (props) => <li className="pl-1" {...props} />,
    blockquote: (props) => <blockquote className="pl-3 my-2 italic" style={{ borderLeft: `2px solid rgba(var(--primary-rgb), ${isDarkTheme ? '0.4' : '0.6'})`, color: `rgba(var(--on-surface-dim-rgb), 0.8)` }} {...props} />,
    code: (props) => <code className="font-mono text-[11px] py-0.5 px-1 rounded-sm" style={{ backgroundColor: isDarkTheme ? `rgba(var(--surface-dark-rgb), 0.5)` : `rgba(var(--primary-rgb), 0.1)`, color: `rgb(var(--${isDarkTheme ? 'primary-light' : 'primary-dark'}-rgb))`, border: `1px solid rgba(var(--primary-rgb), ${isDarkTheme ? '0.1' : '0.2'})` }} {...props} />,
    pre: (props) => <pre className="p-2 my-2 rounded-md text-xs font-mono whitespace-pre-wrap w-full overflow-x-auto" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }} {...props} />,
  }), [isDarkTheme]);

  return (
    <div className="text-xs leading-relaxed text-on-surface-dim break-words">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default ThoughtStream;
